<template>
  <HeadlineComponent>{{ user.full_name }} bearbeiten</HeadlineComponent>
  <Form class="form-horizontal" @submit="onSubmit" v-if="user.id" :initialValues="user">
    <h2>Administratives</h2>
    <div class="form-group mt-2">
      {{ user.first_name }} hat {{ user.shifts ? user.shifts.length : 0 }} Schichten
    </div>
    <div class="form-group">
      <div class="form-check">
        <Field type="checkbox" name="banned" class="form-check-input" :value="true" />
        <label class="form-check-label" for="banned">
          Der User ist gebannt und kann sich nichtmehr einloggen.
        </label>
      </div>
    </div>
    <Field name="role" rules="required" v-slot="{ field, errors, errorMessage, meta }">
      <select v-bind="field" class="form-control"
        :class="{ 'is-invalid': !!errors.length, 'is-valid': meta.valid && meta.dirty }">
        <option :value="1">Crew</option>
        <option :value="2">Rezeption</option>
        <option :value="3">Teamlead</option>
        <option :value="4">Admin</option>
      </select>
      <span class="invalid-feedback" v-if="!!errors.length">{{ errorMessage }}</span>
    </Field>
    <div class="form-group mt-2">
      <span v-if="user.confirmed" class="fa fa-check"></span>
      <span v-if="!user.confirmed" class="fa fa-close"></span>
      Handynummer bestätigt
    </div>
    <div class="form-group mt-2">
      <span v-if="user.email_confirmed" class="fa fa-check"></span>
      <span v-if="!user.email_confirmed" class="fa fa-close"></span>
      E-Mail bestätigt
    </div>
    <div class="form-row">
      <div class="col-sm form-group">
        <label>Kommentar</label>
        <BootstrapField placeholder="Kommentar" name="comment"></BootstrapField>
      </div>
    </div>
    <div class="form-group">
      <div class="form-check">
        <Field type="checkbox" name="allow_multiple_shifts" class="form-check-input" :value="true" />
        <label class="form-check-label" for="allow_multiple_shifts">
          Crewmitglied kann mehr als eine Schicht zur gleichen Zeit belegen
        </label>
      </div>
    </div>
    <template v-if="ticketing_enabled">
      <h2>Tickets</h2>
      <template v-if="user.tickets">
        <template v-for="ticket of user.tickets" :key="ticket.id">
          <h3>Veranstaltung {{ ticket.event.name }}</h3>
          <div v-if="guests_enabled && ticket.guest">
            Zugeordneter Gast: <RouterLink :to="`/events/${ticket.event_id}/guest/${ticket.guest.id}`">{{
              ticket.guest.full_name }}
            </RouterLink> - {{ ticket.guest.email }} - {{ ticket.guest.group }}<br />
            <LoadingButton class="btn-danger" :loading="ticket.loading" @click.prevent="disconnectTicket(ticket)">
              Verbindung aufheben</LoadingButton>
          </div>
          Wird versendet an {{ ticket.email }}
          <div class="form-group mt-2">
            <span v-if="ticket.ticket_sent" class="fa fa-check"></span>
            <span v-if="!ticket.ticket_sent" class="fa fa-close"></span>
            Ticket verschickt
          </div>
          <div class="form-group mt-2" v-if="ticket.ticket_sent">
            <LoadingButton :loading="ticket.loading" @click.prevent="resendTicket(ticket)" class="btn-primary">
              Ticket neu senden</LoadingButton>
          </div>
          <hr />
        </template>
      </template>
    </template>
    <UserForm></UserForm>
    <div class="form-group">
      <input type="submit" value="Speichern" class="btn btn-primary" aria-label="Speichern" />
    </div>
  </Form>
</template>

<script>
import HeadlineComponent from "@/components/HeadlineComponent.vue";
import UserForm from "@/components/UserForm.vue";
import BootstrapField from "@/components/BootstrapField.vue";
import { Field, Form } from "vee-validate";
import userService from "@/services/user.service.js";
import eventService from "@/services/event.service.js";
import { mapActions, mapState } from "pinia";
import { useAlertsStore } from "@/stores/alerts.js";
import router from "@/router/index.js";
import ticketService from "@/services/ticket.service.js";
import LoadingButton from "@/components/LoadingButton.vue";
import { useConfigStore } from "@/stores/config";
import { setErrors } from '@/helper/ErrorToActions.js';
import * as Sentry from '@sentry/vue';

export default {
  name: "UserView",
  components: { BootstrapField, UserForm, HeadlineComponent, Form, Field, LoadingButton },
  data() {
    return {
      user: {},
      tickets: []
    }
  },
  async beforeRouteEnter(to, from, next) {
    let user
    let tickets
    try {
      let response = await userService.get(to.params.id)
      response.data.email2 = response.data.email;
      user = response.data;
    } catch (e) {
      if (e) {
        Sentry.captureException(e);
        useAlertsStore().add('danger', "Es ist was unerwartetes schief gegangen..");
        window.scrollTo(0, 0);
      }
    }
    next(vm => {
      vm.user = user;
      vm.tickets = tickets;
    })
  },
  computed: {
    ...mapState(useConfigStore, ['ticketing_enabled', 'guests_enabled'])
  },
  methods: {
    ...mapActions(useAlertsStore, { addAlert: 'add' }),
    async onSubmit(values, actions) {
      try {
        await userService.update(this.user.id, values);
        this.addAlert('success', 'Der User wurde erfolgreich bearbeitet.');
        window.scrollTo(0, 0);
        router.push('/user');

      } catch (e) {
        if (e) {
          if(e.response.data && e.response.data.errors) {
            setErrors(e.response.data.errors, actions)
            return
          }
          Sentry.captureException(e);
          this.addAlert('danger', "Es ist was unerwartetes schief gegangen..");
          window.scrollTo(0, 0);
        }
      }
      window.scrollTo(0, 0, { smooth: true });
    },
    async disconnectTicket(ticket) {
      ticket.loading = true
      try {
        await eventService.disconnect(ticket.event_id, ticket.id);
        this.addAlert('success', 'Die Ticketverbindung wurde aufgehoben');
        window.scrollTo(0, 0);
        let response = await userService.get(this.user.id)
        response.data.email2 = response.data.email;
        this.user = response.data;
      } catch (e) {
        if (e) {
          console.log(e);
          Sentry.captureException(e);
          this.addAlert('danger', "Es ist was unerwartetes schief gegangen..");
          window.scrollTo(0, 0);
        }
      }
      ticket.loading = false
    },
    async resendTicket(ticket) {
      ticket.loading = true
      try {
        await ticketService.resendTicket(ticket.event_id, ticket.id);
        this.addAlert('success', 'Das Ticket wird neu versandt');
        window.scrollTo(0, 0);
        ticket.ticket_sent = false;
      } catch (e) {
        if (e) {
          Sentry.captureException(e);
          this.addAlert('danger', "Es ist was unerwartetes schief gegangen..");
          window.scrollTo(0, 0);
        }
      }
      ticket.loading = false
    }
  }
}
</script>

<style scoped></style>